import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSetAtom } from "jotai";
import { authStateAtom } from "@/stores/auth.atom";
import { authApi } from "@/services/auth";
import { usersApi, type User } from "@/services/users";
import { getOAuthRedirectUri } from "@/lib/utils";

// Updated types for authentication
interface LoginCredentials {
  username: string;
  password: string;
}

interface LoginResponse {
  success: boolean;
  user?: User;
  error?: string;
}

interface GoogleLoginResponse {
  success: boolean;
  user?: User;
  tokens?: {
    access_token: string;
    refresh_token: string;
  };
  error?: string;
}

/**
 * Authentication hook with real API integration
 */
export function useAuth() {
  const navigate = useNavigate();
  const setAuthState = useSetAtom(authStateAtom);
  const [isLoading, setIsLoading] = useState(false);

  // Get authentication state from localStorage
  const getStoredTokens = useCallback(() => {
    return {
      accessToken: localStorage.getItem("access_token"),
      refreshToken: localStorage.getItem("refresh_token"),
    };
  }, []);

  const isAuthenticated = !!getStoredTokens().accessToken;
  const [user, setUser] = useState<User | null>(null);

  /**
   * Store tokens in localStorage
   */
  const storeTokens = useCallback(
    (accessToken: string, refreshToken: string) => {
      localStorage.setItem("access_token", accessToken);
      localStorage.setItem("refresh_token", refreshToken);
    },
    [],
  );

  /**
   * Clear tokens from localStorage
   */
  const clearTokens = useCallback(() => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
  }, []);

  /**
   * Update auth state with user data
   */
  const updateAuthState = useCallback(
    (user: User | null, authenticated: boolean) => {
      setUser(user);
      setAuthState({
        user,
        isAuthenticated: authenticated,
        isLoading: false,
        error: null,
      });
    },
    [setAuthState],
  );

  /**
   * Login with email/password
   */
  const login = useCallback(
    async (credentials: LoginCredentials): Promise<LoginResponse> => {
      setIsLoading(true);
      try {
        // Get JWT tokens
        const tokenResponse = await authApi.obtainToken({
          username: credentials.username,
          password: credentials.password,
        });

        // Store tokens
        storeTokens(tokenResponse.access, tokenResponse.refresh);

        // Get current user data
        const user = await authApi.getCurrentUser();

        // Update auth state
        updateAuthState(user as User, true);

        // Navigate to location page
        navigate("/location");

        return { success: true, user: user as User };
      } catch (error) {
        console.error("Login error:", error);
        clearTokens();
        updateAuthState(null, false);
        return {
          success: false,
          error: error instanceof Error ? error.message : "Đăng nhập thất bại",
        };
      } finally {
        setIsLoading(false);
      }
    },
    [storeTokens, clearTokens, updateAuthState, navigate],
  );

  /**
   * Google OAuth login
   */
  const loginWithGoogle = useCallback(
    async (authorizationCode: string): Promise<GoogleLoginResponse> => {
      setIsLoading(true);
      try {
        // Exchange code for tokens
        const googleResponse = await authApi.googleAuth(authorizationCode);

        // Store tokens
        storeTokens(googleResponse.access_token, googleResponse.refresh_token);

        // Convert Google response user to our User type
        const user: User = {
          id: googleResponse.user.id,
          username: googleResponse.user.email,
          display_name:
            `${googleResponse.user.first_name} ${googleResponse.user.last_name}`.trim(),
          email: googleResponse.user.email,
          first_name: googleResponse.user.first_name,
          last_name: googleResponse.user.last_name,
          bio: "",
          avatar_url: googleResponse.user.avatar_url || undefined,
          is_staff: false,
          is_verified: false,
          joined_at: new Date().toISOString(),
          last_login: googleResponse.last_login,
          post_count: 0,
          communities: [],
          location: undefined,
        };

        // Update auth state
        updateAuthState(user, true);

        // Navigate to location page
        navigate("/location");

        return {
          success: true,
          user,
          tokens: {
            access_token: googleResponse.access_token,
            refresh_token: googleResponse.refresh_token,
          },
        };
      } catch (error) {
        console.error("Google login error:", error);
        clearTokens();
        updateAuthState(null, false);
        return {
          success: false,
          error:
            error instanceof Error
              ? error.message
              : "Đăng nhập Google thất bại",
        };
      } finally {
        setIsLoading(false);
      }
    },
    [storeTokens, clearTokens, updateAuthState, navigate],
  );

  /**
   * Get Google authorization URL
   */
  const getGoogleAuthUrl = useCallback(async (redirectUri?: string) => {
    try {
      // Use environment-specific redirect URI if none provided
      const finalRedirectUri = redirectUri || getOAuthRedirectUri();
      const response = await authApi.getGoogleUrl(finalRedirectUri);
      return response.url;
    } catch (error) {
      console.error("Error getting Google auth URL:", error);
      throw error;
    }
  }, []);

  /**
   * Login with invitation code (redirect to Google OAuth)
   */
  const loginWithInvitationCode = useCallback(
    async (code: string): Promise<LoginResponse> => {
      try {
        // For invitation codes, redirect to Google OAuth
        // You can store the invitation code in localStorage to use after Google auth
        localStorage.setItem("invitation_code", code);

        const googleUrl = await getGoogleAuthUrl();
        window.location.href = googleUrl;

        return { success: true };
      } catch (error) {
        console.error("Invitation login error:", error);
        return { success: false, error: "Mã mời không hợp lệ" };
      }
    },
    [getGoogleAuthUrl],
  );

  /**
   * Logout function - clears all authentication data
   */
  const logout = useCallback(() => {
    try {
      clearTokens();
      updateAuthState(null, false);

      // Navigate to login page
      navigate("/login");

      return { success: true };
    } catch (error) {
      console.error("Logout error:", error);
      return { success: false, error: "Đăng xuất thất bại" };
    }
  }, [clearTokens, updateAuthState, navigate]);

  /**
   * Get current user data
   */
  const getCurrentUser = useCallback(async () => {
    if (!isAuthenticated) return null;

    try {
      const user = await authApi.getCurrentUser();
      updateAuthState(user as User, true);
      return user as User;
    } catch (error) {
      console.error("Error getting current user:", error);
      // If getting user fails, tokens might be invalid
      logout();
      return null;
    }
  }, [isAuthenticated, updateAuthState, logout]);

  /**
   * Update user profile data
   */
  const updateProfile = useCallback(
    async (updates: Partial<User>) => {
      if (!isAuthenticated) {
        return { success: false, error: "Không tìm thấy thông tin người dùng" };
      }

      try {
        const currentUser = await getCurrentUser();
        if (!currentUser) {
          return {
            success: false,
            error: "Không tìm thấy thông tin người dùng",
          };
        }

        const updatedUser = await usersApi.patchUser(currentUser.id, updates);
        updateAuthState(updatedUser, true);
        return { success: true, user: updatedUser };
      } catch (error) {
        console.error("Error updating profile:", error);
        return {
          success: false,
          error:
            error instanceof Error
              ? error.message
              : "Cập nhật thông tin thất bại",
        };
      }
    },
    [isAuthenticated, getCurrentUser, updateAuthState],
  );

  /**
   * Check authentication status and restore user session
   */
  const checkAuthStatus = useCallback(async () => {
    const tokens = getStoredTokens();

    if (!tokens.accessToken) {
      updateAuthState(null, false);
      return { isAuthenticated: false, user: null };
    }

    try {
      // Verify token and get user data
      await authApi.verifyToken(tokens.accessToken);
      const user = await getCurrentUser();

      return { isAuthenticated: true, user };
    } catch (error) {
      console.error("Auth check failed:", error);
      // Token is invalid, clear it
      logout();
      return { isAuthenticated: false, user: null };
    }
  }, [getStoredTokens, updateAuthState, getCurrentUser, logout]);

  /**
   * Auto-login function for app initialization
   */
  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  return {
    // State
    user,
    isAuthenticated,
    isLoading,

    // Actions
    login,
    loginWithGoogle,
    loginWithInvitationCode,
    getGoogleAuthUrl,
    logout,
    getCurrentUser,
    updateProfile,
    checkAuthStatus,
  };
}
